import React, { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { PatternFormat } from "react-number-format";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useTranslation } from "react-i18next";

interface PhoneNumberFieldProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  required?: boolean;
  shouldUnregister?: boolean;
  requiredMessage?: string;
}

export default function PhoneNumberField<FieldsType extends FieldValues>({
  name,
  label,
  control,
  required,
  shouldUnregister,
  requiredMessage,
  rules,
  inputProps = {},
}: PhoneNumberFieldProps<FieldsType>) {
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      rules={{
        required: requiredMessage || (required && t("Required")),
        validate: (val) => {
          const isNumberValid = isValidPhoneNumber(val, "US");
          if (!isNumberValid) return t("Mobile Number is Not valid");
        },
        ...(rules || {}),
      }}
      render={({
        field: { value, ref, name, onChange },
        fieldState: { error },
      }) => (
        <div className="relative">
          {label && (
            <label className="text-base ml-1 mb-0.5 block" htmlFor={name}>
              <span>
                {t(label)}
                {(required || requiredMessage) && (
                  <span className="text-red-600 ml-0.5 text-base">*</span>
                )}
              </span>
            </label>
          )}
          <PatternFormat
            className="placeholder:text-gray-500 border-gray-200 rounded-lg w-full px-2.5 py-3 border focus:border-brand-hover
            focus-within:border-brand-hover focus:bg-[#eaf1fa]  transition-all focus:ring-0 focus-visible:ring-0 focus-visible:border-brand-hover  outline-none"
            format="(###) ###-####"
            mask="_"
            getInputRef={ref}
            placeholder="(987) 456-3210"
            onChange={onChange}
            {...(inputProps || {})}
            defaultValue={value}
            value={value}
            type="text"
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {t(error?.message)}
          </p>
        </div>
      )}
    />
  );
}
